// LoginNewStyle.js — visual layer for the redesigned login flow ("-new" routes only).
// Responsive STRUCTURE is verbatim from LoginStyle.js (same breakpoints, same Row/Col
// flow, same .ant-row / .ant-col mobile collapse rules). Only the visual layer was
// swapped to match the Figma design (purple outer frame + gradient left card + white
// rounded right card with imported Figma SVG/PNG assets).
//
// Breakpoints follow LoginStyle.js convention so behaviour stays consistent with the
// rest of the app: 1199 / 991 / 768 / 480 / 375.
import styled from 'styled-components';

export const LoginNewWrapper = styled.section`
    font-family: ${({ theme }) => theme.fonts.inter} !important;
    width: 100vw;
    height: 100vh;
    margin: auto;
    position: relative;
    overflow: hidden;
    /* Figma outer purple frame (was the gradient white in old) */
    background: #2E45DA;
    padding: 20px;

    @media (max-width: 991.99px) {
        padding: 16px;
    }

    /* Below md breakpoint — mirror LoginStyle.js: card stays within a small
       purple frame instead of going edge-to-edge so the visual ratio matches
       what the original login flow showed at small sizes. */
    @media (max-width: 768px) {
        padding: 12px;
        height: 100vh;
        display: flex;
        flex-direction: column;
    }

    @media (max-width: 480px) {
        padding: 8px;
    }

    @media (max-width: 375px) {
        padding: 6px;
    }

    /* ===== RIGHT-SIDE WHITE CARD (form) =====
       Wraps the entire right Col. Becomes the visual surface that holds logo +
       form + footer. Mirrors Figma 4876:3230 (white, 832x860, rounded 34). */
    .loginRightCard {
        position: relative;
        background: #ffffff;
        border-radius: 34px;
        height: calc(100vh - 40px);
        overflow: hidden;
        padding: 61px 56px 32px 56px;
        display: flex;
        flex-direction: column;

        /* Concentric-ring glow at the top-right (Figma 4922:3296).
           SVG is the exact asset extracted from Figma — 7 black rings at 0.12 /
           0.15 opacity in a 329x329 viewBox. Positioning mirrors Figma's exact
           placement: 329x329 asset offset by top:-79px, right:-95px so the
           visible portion in the card is 234x250 — identical to Figma's render. */
        &::before {
            content: '';
            position: absolute;
            top: -79px;
            right: -95px;
            width: 329px;
            height: 329px;
            background: url('/images/login-new/glow-topright-rings.svg') no-repeat center / contain;
            pointer-events: none;
            z-index: 0;
        }

        @media (max-width: 991.99px) {
            height: calc(100vh - 32px);
            border-radius: 24px;
            padding: 48px 40px 28px 40px;

            &::before {
                top: -65px;
                right: -78px;
                width: 270px;
                height: 270px;
            }
        }

        @media (max-width: 768px) {
            border-radius: 20px;
            height: calc(100vh - 24px);
            padding: 36px 28px 24px 28px;
        }

        @media (max-width: 480px) {
            border-radius: 16px;
            height: calc(100vh - 16px);
            padding: 28px 20px 20px 20px;

            &::before {
                top: -54px;
                right: -64px;
                width: 220px;
                height: 220px;
            }
        }

        @media (max-width: 375px) {
            border-radius: 12px;
            height: calc(100vh - 12px);
            padding: 22px 14px 18px 14px;
        }
    }

    /* ===== LEFT-SIDE PURPLE CARD (banner) =====
       Mirrors Figma 4876:3221 (548x860, rounded 24, gradient overlay on #2E45DA).
       3 children: rings, tagline+description, dots — distributed by
       space-between so they sit in the upper / middle / lower thirds of the
       card with two equal vertical gaps. Mirrors Figma's vertical rhythm
       (rings y100-520, tagline+desc y570-700, dots y799). */
    .loginLeftCard {
        position: relative;
        height: calc(100vh - 40px);
        border-radius: 24px;
        overflow: hidden;
        background:
            linear-gradient(180deg, rgba(0, 0, 0, 0.2) 0%, rgba(46, 69, 218, 0.2) 100%),
            #2E45DA;
        padding: 70px 56px 56px 56px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 50px;

        /* Short-viewport compaction: when the visible browser area shrinks
           vertically, tighten top/bottom padding + the rings-to-text gap so
           the description never gets clipped at the card's bottom edge. The
           rings illustration itself shrinks via the .loginRings height-aware
           max-width below. */
        @media (max-height: 800px) {
            padding: 32px 56px 32px 56px;
            gap: 28px;
        }

        @media (max-height: 640px) {
            padding: 20px 56px 20px 56px;
            gap: 18px;
        }

        /* Top-right dark ellipse shading (Figma 4922:3256 — imgEllipse15214).
           Figma's PNG export was empty/broken, so we ship a hand-crafted SVG
           that bakes the same soft radial dark gradient (centre-out fade from
           65% black to transparent) used in the design. */
        &::before {
            content: '';
            position: absolute;
            top: -200px;
            right: -200px;
            width: 600px;
            height: 600px;
            background: url('/images/login-new/ellipse-topright-dark.svg') no-repeat center / contain;
            pointer-events: none;
            z-index: 0;
        }

        @media (max-width: 1199.99px) {
            padding: 50px 40px 40px 40px;

            &::before {
                top: -170px;
                right: -170px;
                width: 500px;
                height: 500px;
            }
        }

        @media (max-width: 991.99px) {
            height: calc(100vh - 32px);
            border-radius: 18px;
            padding: 40px 28px 32px 28px;

            &::before {
                top: -140px;
                right: -140px;
                width: 420px;
                height: 420px;
            }
        }

        /* Hide on mobile/tablet — same pattern as LoginStyle .loginBanner */
        @media (max-width: 768px) {
            display: none;
        }
    }

    /* Central illustration container.
       BACKGROUND LAYER (always visible, even before Lottie loads or if it fails):
         - 4 concentric thin dashed rings rendered from rings-bg.svg, applied as
           background-image so they sit BEHIND whatever renders inside the div.
       FOREGROUND LAYER (rendered as a child of this element):
         - <Lottie> animation when new-login.json loads cleanly, OR
         - <img src="Login_static.png"> as fallback when Lottie is missing/broken.
       The rings background guarantees visual continuity between the two states. */
    .loginRings {
        position: relative;
        z-index: 1;
        width: 100%;
        /* Width-capped AND height-capped so the square illustration shrinks
           on short viewports instead of being clipped by .loginLeftCard's
           overflow: hidden. 45vh keeps roughly half the viewport for the
           rings; the rest is reserved for tagline + description + padding. */
        max-width: min(420px, 45vh);
        aspect-ratio: 1 / 1;
        margin: 0 auto;
        flex-shrink: 1;
        min-height: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: url('/images/login-new/rings-bg.svg') no-repeat center / contain;

        @media (max-width: 1199.99px) {
            max-width: min(360px, 42vh);
        }

        @media (max-width: 991.99px) {
            max-width: min(280px, 38vh);
        }

        img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            display: block;
            pointer-events: none;
            user-select: none;
        }
    }

    .loginTaglineGroup {
        position: relative;
        z-index: 1;
        text-align: center;
        width: 100%;
        max-width: 540px;
    }

    @keyframes loginSlideFade {
        from { opacity: 0; transform: translateY(6px); }
        to { opacity: 1; transform: translateY(0); }
    }

    .loginTagline {
        font-family: ${({ theme }) => theme.fonts.inter};
        font-weight: 600;
        font-size: 20px;
        line-height: 1.5;
        letter-spacing: 0.2px;
        color: #ffffff;
        margin: 0 0 14px 0;
        animation: loginSlideFade 500ms ease-out;

        @media (max-width: 1199.99px) {
            font-size: 18px;
        }

        @media (max-width: 991.99px) {
            font-size: 16px;
        }
    }

    .loginDescription {
        font-family: ${({ theme }) => theme.fonts.inter};
        font-weight: 500;
        font-size: 16px;
        line-height: 1.7;
        letter-spacing: 0.16px;
        color: rgba(255, 255, 255, 0.6);
        margin: 0;
        animation: loginSlideFade 500ms ease-out 60ms both;

        @media (max-width: 1199.99px) {
            font-size: 14px;
        }

        @media (max-width: 991.99px) {
            font-size: 13px;
        }
    }

    .loginDots {
        display: flex;
        gap: 10px;
        justify-content: center;
        /* Always reserve space between the description text and the dots so
           the dots never collide with the wrapping description on narrower
           viewports or with longer slide content from the CMS later. */
        margin-top: 32px;

        @media (max-width: 1199.99px) {
            margin-top: 26px;
        }

        @media (max-width: 991.99px) {
            margin-top: 22px;
        }

        @media (max-width: 480px) {
            margin-top: 18px;
        }

        img {
            width: 12px;
            height: 12px;
            display: inline-block;
            cursor: pointer;
            transition: transform 200ms ease-out;
        }

        img:hover {
            transform: scale(1.15);
        }
    }

    /* ===== Logo at top of right card =====
       Same class name as original LoginStyle.js so structure is identical. */
    .loginLogo {
        position: relative;
        z-index: 1;

        @media (max-width: 768px) {
            flex-shrink: 0;
            margin-top: 0;
            margin-bottom: 20px;
        }

        @media (max-width: 480px) {
            margin-bottom: 16px;
        }

        img {
            width: 153px;
            height: 36px;
            object-fit: contain;

            @media (max-width: 480px) {
                width: 130px;
                height: 30px;
            }
        }
    }

    /* ===== Form holder — verbatim translation of LoginStyle .loginContainer =====
       Same responsive rules: vertically centred at every breakpoint (matches
       original LoginStyle which kept justify-content center on mobile too). */
    .loginContainer {
        width: 100%;
        max-width: 400px;
        position: relative;
        z-index: 1;
        margin: auto;
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;

        h1 {
            font-family: ${({ theme }) => theme.fonts.inter};
            /* Figma: Bold 20 #2C2926, centre-aligned */
            font-weight: 700;
            font-size: 20px;
            line-height: 1.3;
            color: #2C2926;
            text-align: center;
            margin: 0 0 8px 0;

            @media (max-width: 991.99px) {
                font-size: 19px;
                margin: 0 0 8px 0;
            }

            @media (max-width: 768px) {
                font-size: 19px;
                margin: 0 0 8px 0;
            }

            @media (max-width: 480px) {
                font-size: 18px;
                margin: 0 0 6px 0;
            }

            @media (max-width: 375px) {
                font-size: 17px;
                margin: 0 0 6px 0;
            }
        }

        p {
            font-family: ${({ theme }) => theme.fonts.inter};
            /* Figma: Medium 16 #485066, centre-aligned */
            font-weight: 500;
            font-size: 16px;
            line-height: 1.5;
            color: #485066;
            text-align: center;
            margin: 0 0 28px 0;

            @media (max-width: 1199.99px) {
                font-size: 14px;
            }

            @media (max-width: 991.99px) {
                font-size: 13px;
                margin-bottom: 24px;
            }

            @media (max-width: 768px) {
                font-size: 14px;
                margin-bottom: 22px;
            }

            @media (max-width: 480px) {
                font-size: 13px;
                margin-bottom: 18px;
            }

            @media (max-width: 375px) {
                font-size: 12px;
                margin-bottom: 16px;
            }

            a {
                font-weight: 600;
                color: #2E45DA;
                white-space: nowrap;
            }

            strong {
                font-weight: 600;
                color: #2C2926;
                word-break: break-word;
            }

            .emailLinkGroup {
                display: inline-block;
                white-space: nowrap;

                @media (max-width: 480px) {
                    white-space: normal;
                }
            }
        }

        .loginBtn {
            /* Figma: solid #2E45DA rounded-12, 52h, white SemiBold 16 */
            background: ${({ theme }) => theme.settings.cta_hover} !important;
            border: 1px solid ${({ theme }) => theme.settings.mainColor} !important;
            border-radius: 12px !important;
            color: #ffffff !important;
            font-family: ${({ theme }) => theme.fonts.inter} !important;
            font-weight: 600 !important;
            font-size: 16px !important;
            height: 52px !important;
            width: 100% !important;
            letter-spacing: 0.16px !important;
            box-shadow:
                0px 6px 14.5px 0px rgba(89, 108, 148, 0.07),
                0px 0.751px 1.816px 0px rgba(89, 108, 148, 0.04) !important;

            &:hover:not(:disabled) {
                background: ${({ theme }) => theme.settings.cta_hover} !important;
                border-color: ${({ theme }) => theme.settings.mainColor} !important;
                color: #ffffff !important;
            }

            @media (max-width: 768px) {
                height: 48px !important;
                font-size: 15px !important;
            }

            @media (max-width: 480px) {
                height: 46px !important;
                font-size: 14px !important;
            }

            @media (max-width: 375px) {
                height: 44px !important;
                font-size: 14px !important;
            }
        }
    }

    /* ===== Form input styling — Figma loginFormV2 (kept the class name from
       original LoginStyle.js so structure is identical). ===== */
    .loginFormV2 {
        .ant-form-item {
            margin-bottom: 16px;
        }

        .ant-form-item-label {
            padding-bottom: 6px;
        }

        .ant-form-item-label > label {
            font-family: ${({ theme }) => theme.fonts.inter};
            font-weight: 600;
            font-size: 12px;
            color: #485066;
            letter-spacing: 0.12px;
            height: auto;
        }

        .ant-form-item-label > label::after {
            display: none;
        }

        .ant-form-item-label > label.ant-form-item-required::before {
            display: none;
        }

        .ant-input,
        .ant-input-password,
        .ant-input-affix-wrapper {
            height: 48px;
            border-radius: 12px;
            border: 1px solid #d4d7e3;
            background: #ffffff;
            padding: 0 16px;
            font-family: ${({ theme }) => theme.fonts.inter};
            font-weight: 500;
            font-size: 14px;
            color: #2C2926;
            letter-spacing: 0.14px;
            box-shadow:
                0px 6px 29px 0px rgba(89, 108, 148, 0.07),
                0px 0.751px 3.631px 0px rgba(89, 108, 148, 0.04);
        }

        .ant-input-affix-wrapper .ant-input {
            height: auto;
            border: none;
            padding: 0;
            background: transparent;
            box-shadow: none;
        }

        /* Figma input prefix icons (envelope 18x18, lock 15x18) */
        .ant-input-prefix {
            margin-right: 12px;
            display: inline-flex;
            align-items: center;

            img {
                width: 18px;
                height: 18px;
                object-fit: contain;
                display: block;
            }
        }

        .ant-input:hover,
        .ant-input-affix-wrapper:hover {
            border-color: #b4a3d8;
        }

        .ant-input:focus,
        .ant-input-focused,
        .ant-input-affix-wrapper-focused {
            border-color: #2E45DA;
            box-shadow: 0 0 0 3px rgba(46, 69, 218, 0.08);
        }

        .ant-input::placeholder,
        .ant-input-affix-wrapper input::placeholder {
            color: #8897ad;
            font-weight: 500;
        }

        /* Disabled state for the email field on the password step.
           Visibly greyed out (saturated grey bg + softened text + dimmed icon)
           with the standard "not-allowed" cursor on hover. Box-shadow stripped
           on the disabled state so it doesn't have the floaty active look. */
        .ant-input.ant-input-disabled,
        .ant-input[disabled],
        .ant-input-affix-wrapper.ant-input-affix-wrapper-disabled {
            background: #e8ebf2 !important;
            color: #6f7891 !important;
            border-color: #d4d7e3 !important;
            cursor: not-allowed !important;
            opacity: 1;
            box-shadow: none !important;
        }

        .ant-input[disabled]:hover,
        .ant-input-affix-wrapper.ant-input-affix-wrapper-disabled:hover {
            border-color: #d4d7e3 !important;
            background: #e8ebf2 !important;
        }

        .ant-input-affix-wrapper-disabled,
        .ant-input-affix-wrapper-disabled * {
            cursor: not-allowed !important;
        }

        .ant-input-affix-wrapper-disabled .ant-input-prefix img {
            opacity: 0.5;
        }

        /* Inner <input> inside the affix wrapper — must be transparent so the
           wrapper's grey shows through edge-to-edge (otherwise the input sits
           as a white strip inside the grey wrapper). */
        .ant-input-affix-wrapper-disabled .ant-input,
        .ant-input-affix-wrapper-disabled input {
            background: transparent !important;
            color: #6f7891 !important;
            -webkit-text-fill-color: #6f7891 !important;
            cursor: not-allowed !important;
        }
    }

    .forgotPassword {
        font-family: ${({ theme }) => theme.fonts.inter};
        font-weight: 600;
        font-size: 16px;
        line-height: 1.2;
        color: #2E45DA;
        text-decoration: underline;

        @media (max-width: 768px) {
            font-size: 14px;
        }

        @media (max-width: 480px) {
            font-size: 13px;
        }

        &:hover {
            color: ${({ theme }) => theme.settings.primary};
        }
    }

    /* Footer pinned at the bottom of the right card (Figma 4876:3250).
       Privacy Policy + Support links removed per request — only "@2026 CrowdVox"
       remains. */
    .loginFooter {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        gap: 20px;
        padding-top: 16px;
        font-family: ${({ theme }) => theme.fonts.inter};
        font-weight: 600;
        font-size: 14px;
        color: #2C2926;
        flex-shrink: 0;

        @media (max-width: 991.99px) {
            font-size: 13px;
        }

        @media (max-width: 480px) {
            font-size: 12px;
            gap: 12px;
            padding-top: 14px;
        }
    }

    /* ===== Status / loader blocks for sibling -new pages ===== */
    .loginStatusBlock {
        text-align: center;
        max-width: 460px;
        margin: 0 auto;

        h1 {
            font-family: ${({ theme }) => theme.fonts.inter};
            font-weight: 700;
            font-size: 22px;
            color: #2C2926;
            margin: 18px 0 8px 0;
        }

        p {
            font-family: ${({ theme }) => theme.fonts.inter};
            font-weight: 500;
            font-size: 14px;
            color: #485066;
            line-height: 1.6;
            margin: 0;
        }

        .loginStatusIcon {
            width: 64px;
            height: 64px;
            border-radius: 50%;
            background: rgba(46, 69, 218, 0.08);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 6px;

            .anticon {
                font-size: 30px;
                color: #2E45DA;
            }
        }
    }

    .loginInfoCard {
        background: #F7F5F0;
        border: 1px solid #e2e8ee;
        border-radius: 12px;
        padding: 14px 16px;
        color: #6D6A67;
        font-size: 12px;
        line-height: 1.6;
        text-align: left;
        margin: 18px 0 22px 0;

        strong {
            color: #2C2926;
            font-weight: 600;
        }
    }

    .loginLink {
        color: #2E45DA;
        font-weight: 600;
        cursor: pointer;

        &.is-disabled {
            color: #aaa;
            cursor: not-allowed;
        }
    }

    .loginMutedLink {
        color: #6D6A67;
        font-size: 12px;
        cursor: pointer;
    }

    .loginResendRow {
        margin-top: -4px;
        margin-bottom: 14px;
        font-size: 13px;
        text-align: right;

        a {
            color: #2E45DA;
            font-weight: 600;
            cursor: pointer;
        }
    }

    /* Override browser autofill background — scoped under wrapper */
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 1000px white inset !important;
        -webkit-text-fill-color: #2C2926 !important;
        transition: background-color 5000s ease-in-out 0s;
    }

    /* Responsive form input font sizes — verbatim from LoginStyle */
    .ant-input,
    .ant-input-password {
        @media (max-width: 768px) {
            font-size: 15px;
        }

        @media (max-width: 480px) {
            font-size: 14px;
        }
    }

    .ant-form-item-label > label {
        @media (max-width: 768px) {
            font-size: 13px;
        }

        @media (max-width: 480px) {
            font-size: 12px;
        }
    }

    /* ===== Row + Col mobile collapse — verbatim from LoginStyle ===== */
    .ant-row {
        height: 100%;

        @media (max-width: 768px) {
            margin-left: 0 !important;
            margin-right: 0 !important;
            flex: 1;
            display: flex;
            flex-direction: column;
            height: auto;
        }
    }

    .ant-col {
        height: 100%;

        @media (max-width: 768px) {
            padding-left: 0 !important;
            padding-right: 0 !important;
            flex: none !important;
            max-width: 100% !important;
            width: 100% !important;
            height: auto;
        }

        &:first-child {
            @media (max-width: 768px) {
                flex: 1 !important;
                display: flex;
                flex-direction: column;
            }
        }
    }
`;
